import React from "react";

import { useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import { useDispatch } from "react-redux";

import { BodyMedium } from "Cobalt/BodyText";
import { openModal, closeModal } from "Cobalt/Modal";
import Button from "Common/Button";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import { usePartnerContext } from "Common/PartnerContext";

import ClickableFilename from "./ClickableFilename";
import { DONT_HAVE_FILE_FILENAME, SUCCESS_MODAL_NAME, WAGE_DOC_ISSUE_MODAL_NAME } from "./constants";
import HowDoIGetThis from "./HowDoIGetThis";
import { useDocumentPickerContext, bem, parseDocType, responsiveBem, getModalName } from "./utils";
import WhyIsThisRequired from "./WhyIsThisRequired";

// TODO. This could be better, but it is currently overly coupled with
// the mainDocumentState. Will get to this once alternatives and
// explanation features are separated.
const PayrollWageReportSync = () => {
  const {
    docType,
    docKey,
    mainDocumentState: { setError, setFilename, syncPartnerDocument },
  } = useDocumentPickerContext();
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const handleClickSyncPartnerDocument = async () => {
    const resp = await syncPartnerDocument({
      companyPolicyId,
      doc_type: docType,
      doc_key: docKey,
    });

    if (resp.data.errors) {
      setError("We were unable to retrieve your document. Click below to upload.");
    } else {
      setFilename(resp.data.file_path);
    }
  };

  return (
    <Button color="white" onClick={handleClickSyncPartnerDocument}>
      <Icon icon="sync-alt" mr={1} flavor="solid" /> Retrieve Document from Payroll
    </Button>
  );
};

const MainDocumentSection = () => {
  const {
    index,
    fileInputName,
    meta,
    title,
    originalFilename,
    mainDocumentState: { filename, error, uploadedInvalidDocument, setUploadedInvalidDocument, uploadFile },

    onDocumentUploadSuccess,
    validateDocument,
    showWhyRequired,
    pickerState: { processing },
    alternativeDocumentsState: { altDocsCardIsOpen },
  } = useDocumentPickerContext();

  const { partnerHasWageReport } = usePartnerContext();

  const statusMessage =
    error ||
    (altDocsCardIsOpen
      ? undefined
      : originalFilename && originalFilename === filename
        ? "Upload Successful"
        : filename
          ? "File uploaded successfully"
          : undefined);

  const information = meta && meta.information ? meta.information : "";
  const docType = parseDocType(fileInputName);

  const dispatch = useDispatch();
  const handleFileChange = async (e) => {
    e.persist();
    dispatch(closeModal());
    const file = e.target.files[0];
    const response = await uploadFile(file);
    const data = await response.json();
    if (validateDocument) {
      if (uploadedInvalidDocument) {
        if (response.status === 200) {
          dispatch(openModal(SUCCESS_MODAL_NAME));
          setUploadedInvalidDocument(false);
        }
      } else {
        const isInvalidDocument = data?.errors?.find(({ title }) => title === "document_invalid");

        if (isInvalidDocument) {
          setUploadedInvalidDocument(true);
          dispatch(openModal(getModalName(WAGE_DOC_ISSUE_MODAL_NAME, index)));
        }
      }
    }

    onDocumentUploadSuccess(data);
    return response;
  };
  return processing ? null : (
    <Row className={bem("MainDocumentContent")}>
      {/* disable main content by overlaying a semi-transparent div when
           alternative documents section is open */}
      {altDocsCardIsOpen && <div className={bem("Overlay")} />}
      <Col
        md={8}
        lg={9}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <BodyMedium weight="bold" mb={3}>
          {title}
        </BodyMedium>
        {/* For additional required documents, we want to provide more information (e.g. want to be more up-front about why), show the reason why a doc is required */}
        {information && <BodyMedium mb={3}>{information}</BodyMedium>}
        {showWhyRequired && meta.whyRequired && (
          <BodyMedium className="italic" mb={3}>
            {meta.whyRequired}
          </BodyMedium>
        )}
        {statusMessage && (
          <BodyMedium>
            <p className={responsiveBem(error ? "Failure" : "Success")}>
              <Icon {...(error ? { icon: "times", flavor: "solid" } : { icon: "check" })} pr="sm" style={{ marginRight: "2px" }} />

              {statusMessage}
            </p>
          </BodyMedium>
        )}

        {partnerHasWageReport && docType == "state_wage_report" && !error ? (
          <PayrollWageReportSync />
        ) : (
          <ClickableFilename htmlFor={`input-${fileInputName}`} filename={filename?.endsWith(DONT_HAVE_FILE_FILENAME) ? "(I don't have this)" : filename} />
        )}

        <input type="file" name={fileInputName} className={bem("RealFilePicker")} onChange={handleFileChange} id={`input-${fileInputName}`} />
      </Col>
      <Col md={4} lg={3}>
        <HowDoIGetThis />
        {/* Only add the modal if we're not showing the reason why */}
        {!showWhyRequired && <WhyIsThisRequired />}
      </Col>
    </Row>
  );
};

export default MainDocumentSection;
